import { pb } from './pocketbase';
import { createNotification } from './notification.service';

// URL de base pour l'API backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const getStudentDocuments = async (studentId, options = {}) => {
    try {
        const queryOptions = {
            filter: `student_id = "${studentId}"`,
            sort: options.sort || '-created',
            expand: options.expand || '',
            page: options.page || 1,
            perPage: options.perPage || 50
        };

        // Utiliser directement la collection student_documents
        const result = await pb.collection('student_documents').getList(
            queryOptions.page,
            queryOptions.perPage,
            {
                filter: queryOptions.filter,
                sort: queryOptions.sort,
                expand: queryOptions.expand
            }
        );

        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des documents:', error);
        return {
            items: [],
            totalItems: 0,
            totalPages: 0,
            page: 1
        };
    }
};


export const getProspectDocuments = async (prospectId, options = {}) => {
    try {
        const queryOptions = {
            filter: `prospect_id = "${prospectId}"`,
            sort: options.sort || '-created',
            expand: options.expand || '',
            page: options.page || 1,
            perPage: options.perPage || 50
        };

        // Utiliser directement la collection prospect_documents
        const result = await pb.collection('prospect_documents').getList(
            queryOptions.page,
            queryOptions.perPage,
            {
                filter: queryOptions.filter,
                sort: queryOptions.sort,
                expand: queryOptions.expand
            }
        );

        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des documents prospects:', error);
        return {
            items: [],
            totalItems: 0,
            totalPages: 0,
            page: 1
        };
    }
};

export const downloadDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de télécharger ce document : document invalide");
        return false;
    }

    try {
        // Télécharger le fichier avec fetch sans rediriger
        const response = await fetch(`${API_BASE_URL}/api/documents/download/${document.id}`, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('pocketbase_auth')).token}`
            }
        });

        if (!response.ok) {
            throw new Error(`Erreur lors du téléchargement: ${response.statusText}`);
        }

        // Extraire le nom du fichier depuis l'en-tête Content-Disposition si disponible
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = document.file || 'document';

        if (contentDisposition) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDisposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]|;$/g, '');
            }
        }

        // Convertir la réponse en blob
        const blob = await response.blob();

        // Créer une URL objet pour le blob
        const blobUrl = window.URL.createObjectURL(blob);

        // Créer un lien invisible et simuler un clic pour télécharger
        const link = window.document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename);
        link.style.display = 'none';
        window.document.body.appendChild(link);
        link.click();

        // Nettoyer
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl); // Libérer la mémoire

        return true;
    } catch (error) {
        console.error("Erreur lors du téléchargement:", error);
        return false;
    }
};

export const ConfirmDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de confirmer ce document");
        return false;
    }

    try {
        // Mise à jour avec le champ 'statut'
        await pb.collection('student_documents').update(document.id, {
            statut: 'ok'
        });

        return true;
    } catch (error) {
        console.error("Erreur lors de la confirmation du document");
        return false;
    }
};

export const RefuseDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de refuser ce document");
        return false;
    }

    try {
        await pb.collection('student_documents').update(document.id, {
            statut: 'ko'
        });

        return true;
    } catch (error) {
        console.error("Erreur lors du refus du document");
        return false;
    }
};

export const uploadDocument = async (studentId, file, type, partnerId = null) => {
    if (!studentId || !file || !type) {
        console.error("Impossible d'uploader ce document: données manquantes");
        return false;
    }

    try {
        const formData = new FormData();
        formData.append('student_id', studentId);
        formData.append('file', file);
        formData.append('type', type);
        formData.append('statut', 'pending'); 

        // Ajouter le partner_id si fourni, sinon essayer de le récupérer depuis l'utilisateur connecté
        if (partnerId) {
            formData.append('partner_id', partnerId);
        } else if (pb.authStore.isValid && pb.authStore.model?.partner_id) {
            formData.append('partner_id', pb.authStore.model.partner_id);
        }

        const result = await pb.collection('student_documents').create(formData);

        return result;
    } catch (error) {
        console.error("Erreur lors de l'upload du document:", error);
        return false;
    }
};

export const submitDocuments = async (studentId) => {
    if (!studentId) {
        console.error("Impossible de soumettre les documents: studentId manquant");
        return false;
    }

    try {
        const pendingDocuments = await pb.collection('student_documents').getList(1, 100, {
            filter: `student_id = "${studentId}" && statut = "pending"`,
        });

        if (pendingDocuments.items.length === 0) {
            console.log("Aucun document en attente à soumettre");
            return true; // Pas d'erreur, simplement rien à faire
        }

        // Mettre à jour le statut de tous les documents en attente
        const updatePromises = pendingDocuments.items.map(doc =>
            pb.collection('student_documents').update(doc.id, {
                statut: 'submitted' // Nouveau statut: soumis pour validation par l'administrateur
            })
        );

        await Promise.all(updatePromises);

        // Créer une notification pour les administrateurs
        try {
            const student = await pb.collection('students').getOne(studentId);

            const notificationData = {
                type: "documents_submitted",
                title: "Documents soumis pour validation",
                message: `L'étudiant ${student.first_name} ${student.last_name} a soumis ${pendingDocuments.items.length} document(s).`,
                sender_id: studentId,
                sender_type: "student",
                icon: "user_logo.png",
                link: `/admin/students/${studentId}`,
                read: false
            };

            await createNotification(notificationData);
        } catch (error) {
            console.error("Erreur lors de la création de la notification pour la soumission de documents:", error);
        }

        return true;
    } catch (error) {
        console.error("Erreur lors de la soumission des documents:", error);
        return false;
    }
};

export const deleteDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de supprimer ce document");
        return false;
    }

    try {
        await pb.collection('student_documents').delete(document.id);
        return true;
    } catch (error) {
        console.error("Erreur lors de la suppression du document");
        return false;
    }
};


export const ConfirmProspectDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de confirmer ce document prospect");
        return false;
    }

    try {
        await pb.collection('prospect_documents').update(document.id, {
            statut: 'ok'
        });

        return true;
    } catch (error) {
        console.error("Erreur lors de la confirmation du document prospect");
        return false;
    }
};

export const RefuseProspectDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de refuser ce document prospect");
        return false;
    }

    try {
        await pb.collection('prospect_documents').update(document.id, {
            statut: 'ko'
        });

        return true;
    } catch (error) {
        console.error("Erreur lors du refus du document prospect");
        return false;
    }
};

export const deleteProspectDocument = async (document) => {
    if (!document || !document.id) {
        console.error("Impossible de supprimer ce document prospect");
        return false;
    }

    try {
        await pb.collection('prospect_documents').delete(document.id);
        return true;
    } catch (error) {
        console.error("Erreur lors de la suppression du document prospect");
        return false;
    }
};

export const uploadProspectDocument = async (prospectId, file, type) => {
    if (!prospectId || !file || !type) {
        console.error("Impossible d'uploader ce document prospect: données manquantes");
        return false;
    }

    try {
        const formData = new FormData();
        formData.append('prospect_id', prospectId);
        formData.append('file', file);
        formData.append('type', type);
        formData.append('partner_id', pb.authStore.model.partner_id);
        formData.append('statut', 'ok'); // Statut "ok" par défaut pour les prospects
        
        // Ajouter le partner_id de l'utilisateur connecté
        if (pb.authStore.isValid && pb.authStore.model?.partner_id) {
            formData.append('partner_id', pb.authStore.model.partner_id);
        }
        
        const result = await pb.collection('prospect_documents').create(formData);

        return result;
    } catch (error) {
        console.error("Erreur lors de l'upload du document prospect:", error);
        return false;
    }
};

